import { forwardRef, useEffect, useRef, useState, type ChangeEvent, type TextareaHTMLAttributes } from 'react';
import { flushPastes, pendingPastes, queuePaste } from './api';
import type { Session } from './types';
import { ErrorNotice } from './components';
import { readCopyReceipt, writeCopyReceipt } from './clipboardReceipts';
import { fieldPasteParents, fieldPasteRanges, markFieldPaste, captureFieldEdit } from './accountPasteAudit';

export const AuditedTextarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & {
  sessionId: string;
  destination: string;
  onRecorded?: (session: Session) => void;
}>(({ sessionId, destination, onRecorded, onChange, onPaste, ...props }, ref) => {
  const [unknown, setUnknown] = useState(false);
  const [error, setError] = useState('');
  const [, update] = useState(0);
  const recorded = useRef(onRecorded);
  recorded.current = onRecorded;
  const sync = async () => {
    try {
      const saved = await flushPastes(sessionId);
      if (saved) recorded.current?.(saved as Session);
      setError('');
    } catch { setError('Your pasted text is kept. Its provenance record is waiting to sync.'); }
  };
  useEffect(() => {
    const changed = () => update(value => value + 1);
    const online = () => { void sync(); };
    window.addEventListener('ig-paste-sync', changed);
    window.addEventListener('online', online);
    if (pendingPastes(sessionId).length) void sync();
    return () => {
      window.removeEventListener('ig-paste-sync', changed);
      window.removeEventListener('online', online);
    };
  }, [sessionId]);
  const waiting = pendingPastes(sessionId).filter(paste => paste.destination === destination);
  return <>
    <textarea {...props} ref={ref} data-audited-paste="true" readOnly={props.readOnly}
      onChange={onChange}
      onCopy={event => {
        const field = event.currentTarget;
        const text = field.value.slice(field.selectionStart, field.selectionEnd);
        writeCopyReceipt(event.clipboardData, { text, sessionId,
          ranges:fieldPasteRanges(field,field.selectionStart,field.selectionEnd),
          ...fieldPasteParents(field, field.selectionStart, field.selectionEnd) });
        event.preventDefault();
      }}
      onPaste={event => {
        event.preventDefault();
        if (props.disabled || props.readOnly) return;
        const field = event.currentTarget;
        const text = event.clipboardData.getData('text/plain');
        if (!text) return;
        const receipt = readCopyReceipt(event.clipboardData);
        const tracked = receipt?.text === text && (!receipt.sessionId || receipt.sessionId === sessionId);
        const before = field.value, start = field.selectionStart, end = field.selectionEnd;
        const next = before.slice(0, start) + text + before.slice(end);
        const requestId = crypto.randomUUID();
        if (props.maxLength && next.length > props.maxLength) {
          setError(`This paste exceeds the ${props.maxLength} character limit.`); return;
        }
        try {
          queuePaste(sessionId, {
            text, destination, method: 'clipboard', origin: tracked ? 'document' : 'external',
            requestId, startOffset: start,
            ...(receipt?.sessionId === sessionId && receipt.parentPasteIds?.length ? { parentPasteIds: receipt.parentPasteIds } : {}),
            ...(receipt?.parentInputPasteIds?.length ? { parentInputPasteIds: receipt.parentInputPasteIds } : {}),
          });
        } catch { setError('Device recovery is unavailable. Keep this page open until the paste record is synced.'); }
        onPaste?.(event);
        captureFieldEdit(field,start,end);
        field.value = next;
        if (!tracked) markFieldPaste(field,requestId,start,text.length,false);
        else if (receipt?.ranges) {
          for (const range of receipt.ranges)
            markFieldPaste(field,requestId,start+range.startOffset,range.endOffset-range.startOffset,false);
        } else if (receipt?.parentPasteIds?.length || receipt?.parentInputPasteIds?.length)
          markFieldPaste(field,requestId,start,text.length,false);
        onChange?.({ target: field, currentTarget: field } as ChangeEvent<HTMLTextAreaElement>);
        field.setSelectionRange(start + text.length, start + text.length);
        setUnknown(current => current || !tracked);
        void sync();
      }}
    />
    {(unknown || waiting.some(paste => paste.origin === 'external')) && <p className="resource-disclosure" role="status"><strong>External paste — lineage unknown.</strong> Pasting is allowed. The original text is retained; editing or adding a reference does not remove this flag.</p>}
    {waiting.length > 0 && <p className="field-hint" role="status">{waiting.length} paste records waiting to sync.</p>}
    {error && <ErrorNotice message={error} retry={() => void sync()} />}
  </>;
});
